import * as React from "react";
import { CloudCheckIcon } from "../../../components/icons";
import { Alert } from "../../../components/ui/alert";
import { Button } from "../../../components/ui/button";
import { Progress, Spinner } from "../../../components/ui/feedback";
import { useSaveDraftContentMutation } from "../api/checkoutApi";
import { DocumentCheckOutAdapter } from "../../../platform";
import { useCheckOutVerification, verificationMessage } from "../hooks/useCheckOutVerification";
import { describeError } from "../../../utils/describeError";

// Save takes zero input: the ribbon press verifies the document's identity part
// against the API, pushes the live bytes to the draft, and reports — all inside a
// strip at the foot of the pane, so the draft and its chat are never disturbed.
// Press it, see the result, carry on. (A stale part is reported by name — "no longer
// open" — never an obscure mid-save failure.)

export interface SaveViewProps {
  /** Passed in rather than read from context: a host without one never renders this. */
  documents: DocumentCheckOutAdapter;
  /** Dismisses the strip. Success dismisses itself; everything else waits. */
  onClose?: () => void;
}

const SaveView = ({ documents, onClose }: SaveViewProps) => {
  const verification = useCheckOutVerification(documents);
  const [saveDraft] = useSaveDraftContentMutation();
  const [progress, setProgress] = React.useState<number | null>(null);
  const [result, setResult] = React.useState<"idle" | "saving" | "saved" | "failed">("idle");
  const [failureDetail, setFailureDetail] = React.useState<string | null>(null);
  const startedRef = React.useRef(false);

  const runSave = React.useCallback(async () => {
    if (verification.status !== "ok" || !verification.identity) return;
    setResult("saving");
    setFailureDetail(null);
    try {
      const content = await documents.getDocumentBytes((fraction) => setProgress(fraction));
      setProgress(null);
      await saveDraft({
        projectObjectIdentifier: verification.identity.projectObjectIdentifier,
        documentObjectIdentifier: verification.identity.documentObjectIdentifier,
        content,
        extension: documents.fileExtension,
      }).unwrap();
      setResult("saved");
    } catch (error) {
      setProgress(null);
      setFailureDetail(describeError(error));
      setResult("failed");
    }
  }, [verification.status, verification.identity, saveDraft, documents]);

  // A clean save needs no acknowledgement — let it fade. Failures and blocked
  // states stay up until the user dismisses them.
  React.useEffect(() => {
    if (result !== "saved" || !onClose) return undefined;
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [result, onClose]);

  // Some host/platform pairs cannot hand us the document's bytes at all (Word on the
  // web, for one). Say so before starting rather than failing at the last step of a
  // long save.
  const saveUnavailable = documents.describeSaveUnavailable();

  // Auto-run once when verification lands on ok — the button was the input.
  React.useEffect(() => {
    if (saveUnavailable) return;
    if (verification.status === "ok" && !startedRef.current) {
      startedRef.current = true;
      void runSave();
    }
  }, [verification.status, runSave, saveUnavailable]);

  // Verification gates only the pre-save screen. Saving invalidates the check-out
  // states, so once a result exists it must not be replaced by whatever the refetch
  // says — reporting "no longer checked out" over the top of "Saved" reads as failure.
  if (result === "idle") {
    if (saveUnavailable) {
      return (
        <div className="flex min-w-0 flex-1 flex-col gap-3 p-3">
          <Alert severity="info">{saveUnavailable}</Alert>
        </div>
      );
    }

    if (verification.status === "loading") {
      return (
        <div className="flex flex-1 justify-center p-4">
          <Spinner size={24} />
        </div>
      );
    }

    if (verification.status !== "ok") {
      // A failed read is the one non-ok state that is worth retrying in place: we do
      // not know that anything is wrong with the document, only that the host would not
      // answer. Every other state is a fact about the draft and a retry cannot change
      // it, so it gets no button.
      const retryable = verification.status === "read-failed";
      return (
        <div className="flex min-w-0 flex-1 flex-col gap-3 p-3">
          <Alert severity={verification.status === "no-identity" ? "info" : "warning"}>
            {verification.readFailure ??
              verificationMessage(verification.status, verification.state)}
          </Alert>
          {retryable && (
            <Button variant="outline" onClick={verification.refresh}>
              Try again
            </Button>
          )}
        </div>
      );
    }
  }

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-3 p-3">
      {result === "saving" && (
        <>
          <p className="text-body2">Saving to Jylo…</p>
          <Progress value={progress !== null ? progress * 100 : undefined} />
        </>
      )}
      {result === "saved" && (
        <div className="flex flex-row items-start gap-2">
          <CloudCheckIcon className="mt-px text-success" />
          <div>
            <p className="text-body2 font-medium text-strong">
              Saved to Jylo at{" "}
              {new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </p>
            <div className="text-caption text-muted-foreground">
              No version was created — check in when you are done.
            </div>
          </div>
        </div>
      )}
      {result === "failed" && (
        <div className="flex flex-col gap-2">
          <Alert severity="error">
            Saving to Jylo failed. Your local document is untouched.
            {failureDetail && <div className="mt-1 text-caption break-words">{failureDetail}</div>}
          </Alert>
          <Button variant="outline" onClick={() => void runSave()}>
            Try again
          </Button>
        </div>
      )}
    </div>
  );
};

export default SaveView;
